import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Eye, Sparkles, Save, FolderOpen, Trash2, Mail, Loader2, Clock, Users, X } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "../../lib/api";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "link"];

const TONES = [
  { value: "warm", label: "Warm & herzlich" },
  { value: "informativ", label: "Informativ & klar" },
  { value: "locker", label: "Locker & humorvoll" },
  { value: "poetisch", label: "Poetisch & naturverbunden" },
];

const emptyDraft = () => ({ subject: "", title: "", body_html: "", cta_text: "", cta_url: "" });

export default function BroadcastsAdmin() {
  const [draft, setDraft] = useState(emptyDraft());
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  // AI draft
  const [aiOpen, setAiOpen] = useState(false);
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiTone, setAiTone] = useState("warm");
  const [aiLoading, setAiLoading] = useState(false);

  // Templates
  const [tplsOpen, setTplsOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  const canSend = useMemo(
    () => draft.subject.trim().length >= 2 && (draft.body_html || "").replace(/<[^>]*>/g, "").trim().length > 0,
    [draft.subject, draft.body_html],
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      const [h, t, s] = await Promise.all([
        api.get("/admin/broadcasts").then((r) => r.data).catch(() => []),
        api.get("/admin/broadcast-templates").then((r) => r.data).catch(() => []),
        api.get("/admin/newsletter/subscribers").then((r) => r.data).catch(() => []),
      ]);
      setHistory(h || []);
      setTemplates(t || []);
      setSubCount((s || []).filter((x) => x.active).length);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // Preview
  const openPreview = async () => {
    try {
      const r = await api.post("/admin/broadcasts/preview", draft);
      setPreviewHtml(r.data.html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview fehlgeschlagen");
    }
  };

  // Test send
  const doTest = async () => {
    if (!testEmail.trim()) return toast.error("Bitte Test-Email eintragen");
    setTesting(true);
    try {
      const r = await api.post("/admin/broadcasts/test-send", { ...draft, to: testEmail.trim() });
      if (r.data.ok || r.data.dry_run) toast.success(`Test verschickt an ${testEmail}${r.data.dry_run ? " (SMTP dry-run)" : ""}`);
      else toast.error(r.data.error || "Test fehlgeschlagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  // Real send
  const doSend = async () => {
    if (!canSend) return toast.error("Betreff & Inhalt fehlen");
    if (!window.confirm(`Wirklich an ${subCount} Abonnent:innen versenden?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/broadcasts/send", draft);
      toast.success(`Versand gestartet – ${r.data.target_count} Empfänger, läuft im Hintergrund.`);
      setDraft(emptyDraft());
      setTimeout(loadAll, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  // AI draft
  const doAiDraft = async () => {
    if (aiKeywords.trim().length < 3) return toast.error("Bitte ein paar Stichworte eintragen");
    setAiLoading(true);
    try {
      const r = await api.post("/admin/broadcasts/ai-draft", { keywords: aiKeywords.trim(), tone: aiTone });
      setDraft({
        subject: r.data.subject || "",
        title: r.data.title || "",
        body_html: r.data.body_html || "",
        cta_text: r.data.cta_text || "",
        cta_url: r.data.cta_url || "",
      });
      setAiOpen(false);
      setAiKeywords("");
      toast.success("KI-Entwurf übernommen – jetzt gerne noch anpassen und Vorschau prüfen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Entwurf fehlgeschlagen");
    } finally {
      setAiLoading(false);
    }
  };

  // Templates
  const saveTemplate = async () => {
    if (!tplName.trim()) return toast.error("Bitte Vorlagen-Name angeben");
    try {
      await api.post("/admin/broadcast-templates", { name: tplName.trim(), ...draft });
      toast.success("Vorlage gespeichert");
      setTplName("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const applyTemplate = (t) => {
    setDraft({
      subject: t.subject || "",
      title: t.title || "",
      body_html: t.body_html || "",
      cta_text: t.cta_text || "",
      cta_url: t.cta_url || "",
    });
    setTplsOpen(false);
    toast.success(`Vorlage „${t.name}" geladen`);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Vorlage wirklich löschen?")) return;
    await api.delete(`/admin/broadcast-templates/${id}`);
    loadAll();
  };

  return (
    <div className="space-y-6" data-testid="broadcasts-admin-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">Broadcast</h2>
          <p className="text-sm opacity-70">Freie E-Mails an deine Newsletter-Abonnenten – Termine, Ankündigungen, Angebote …</p>
        </div>
        <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-full" style={{ backgroundColor: "var(--wp-cream)" }}>
          <Users className="w-4 h-4" style={{ color: "var(--wp-olive)" }} />
          <span data-testid="broadcasts-sub-count"><b>{subCount}</b> aktive Abonnent:innen</span>
        </div>
      </div>

      {/* Compose card */}
      <div className="plant-card p-5 space-y-4" data-testid="broadcasts-compose">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setAiOpen(true)} className="btn-secondary text-sm" data-testid="broadcasts-ai-btn">
            <Sparkles className="w-4 h-4" /> KI-Entwurf
          </button>
          <button onClick={() => setTplsOpen(true)} className="btn-secondary text-sm" data-testid="broadcasts-templates-btn">
            <FolderOpen className="w-4 h-4" /> Vorlagen ({templates.length})
          </button>
          {(draft.subject || draft.body_html) && (
            <button onClick={() => setDraft(emptyDraft())} className="btn-secondary text-sm ml-auto" data-testid="broadcasts-clear-btn">
              <X className="w-4 h-4" /> Leeren
            </button>
          )}
        </div>

        <label className="block">
          <div className="overline mb-1.5">Betreff *</div>
          <input
            value={draft.subject}
            onChange={(e) => update({ subject: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--wp-border)" }}
            placeholder="z. B. Neuer Termin: Kräuterwanderung am 15. Mai"
            data-testid="broadcasts-subject"
          />
        </label>

        <label className="block">
          <div className="overline mb-1.5">Überschrift in der Mail (optional)</div>
          <input
            value={draft.title}
            onChange={(e) => update({ title: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--wp-border)" }}
            placeholder="Standardmäßig gleich wie Betreff"
            data-testid="broadcasts-title"
          />
        </label>

        <div>
          <div className="overline mb-1.5">Inhalt *</div>
          <div className="rounded-lg border" style={{ borderColor: "var(--wp-border)", backgroundColor: "white" }} data-testid="broadcasts-editor">
            <ReactQuill
              theme="snow"
              value={draft.body_html}
              onChange={(v) => update({ body_html: v })}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Schreibe hier deinen Text … (fett, kursiv, Listen und Links via Toolbar)"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="overline mb-1.5">CTA-Button Text (optional)</div>
            <input
              value={draft.cta_text}
              onChange={(e) => update({ cta_text: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border"
              style={{ borderColor: "var(--wp-border)" }}
              placeholder='z. B. "Jetzt anmelden"'
              data-testid="broadcasts-cta-text"
            />
          </label>
          <label className="block">
            <div className="overline mb-1.5">CTA-Button URL</div>
            <input
              value={draft.cta_url}
              onChange={(e) => update({ cta_url: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border"
              style={{ borderColor: "var(--wp-border)" }}
              placeholder="https://…"
              data-testid="broadcasts-cta-url"
            />
          </label>
        </div>

        {/* Actions */}
        <div className="pt-2 border-t space-y-3" style={{ borderColor: "var(--wp-border)" }}>
          <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Test-Email eintragen"
              className="flex-1 px-3 py-2 rounded-lg border"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="broadcasts-test-email"
            />
            <button onClick={doTest} disabled={testing || !canSend} className="btn-secondary text-sm" data-testid="broadcasts-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Test senden
            </button>
            <button onClick={openPreview} disabled={!canSend} className="btn-secondary text-sm" data-testid="broadcasts-preview-btn">
              <Eye className="w-4 h-4" /> Vorschau
            </button>
            <button onClick={doSend} disabled={sending || !canSend || subCount === 0} className="btn-primary text-sm" data-testid="broadcasts-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              An {subCount} senden
            </button>
          </div>
          <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
            <input
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              placeholder='Als Vorlage speichern (z. B. "Terminankündigung")'
              className="flex-1 px-3 py-2 rounded-lg border"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="broadcasts-tpl-name"
            />
            <button onClick={saveTemplate} disabled={!tplName.trim()} className="btn-secondary text-sm" data-testid="broadcasts-tpl-save">
              <Save className="w-4 h-4" /> Vorlage speichern
            </button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="plant-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg flex items-center gap-2"><Clock className="w-4 h-4" /> Versendet</h3>
        </div>
        {loading ? (
          <div className="text-sm opacity-60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>
        ) : history.length === 0 ? (
          <div className="text-sm opacity-60">Noch keine Broadcasts versendet.</div>
        ) : (
          <div className="space-y-2" data-testid="broadcasts-history">
            {history.map((b) => (
              <div key={b.id} data-testid={`broadcast-history-item-${b.id}`} className="p-3 rounded-lg border grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 md:gap-4 items-center" style={{ borderColor: "var(--wp-border)" }}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.subject}</div>
                  <div className="text-[11px] opacity-60">{new Date(b.created_at || b.sent_at).toLocaleString("de-LU")}</div>
                </div>
                <div className="text-xs px-2 py-1 rounded-full text-center" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-primary)" }}>
                  {b.status === "sending" ? "läuft …" : b.status === "sent" ? "gesendet" : b.status}
                </div>
                <div className="text-xs opacity-70 whitespace-nowrap">↑ {b.recipient_count || 0}{b.target_count ? ` / ${b.target_count}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setPreviewOpen(false)} data-testid="broadcasts-preview-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
              <h3 className="font-display text-xl">Vorschau</h3>
              <button onClick={() => setPreviewOpen(false)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe title="preview" srcDoc={previewHtml} className="w-full min-h-[70vh] border-0" />
            </div>
          </div>
        </div>
      )}

      {/* AI-Draft modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setAiOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="broadcasts-ai-modal">
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
              <h3 className="font-display text-xl flex items-center gap-2"><Sparkles className="w-5 h-5" /> KI-Entwurf</h3>
              <button onClick={() => setAiOpen(false)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <div className="overline mb-1.5">Worum geht's? (Stichworte)</div>
                <textarea
                  value={aiKeywords}
                  onChange={(e) => setAiKeywords(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ borderColor: "var(--wp-border)" }}
                  placeholder="z. B. Kräuterwanderung 15. Mai in Diekirch, 14 Uhr, kostenlos, Bärlauch & Giersch bestimmen, Anmeldung erforderlich"
                  data-testid="broadcasts-ai-keywords"
                />
              </label>
              <label className="block">
                <div className="overline mb-1.5">Ton</div>
                <select
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-white text-sm"
                  style={{ borderColor: "var(--wp-border)" }}
                  data-testid="broadcasts-ai-tone"
                >
                  {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
            </div>
            <div className="px-5 py-3 border-t flex items-center gap-2 justify-end" style={{ borderColor: "var(--wp-border)" }}>
              <button onClick={() => setAiOpen(false)} className="btn-secondary text-sm">Abbrechen</button>
              <button onClick={doAiDraft} disabled={aiLoading} className="btn-primary text-sm" data-testid="broadcasts-ai-generate">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Entwurf erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates modal */}
      {tplsOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setTplsOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="broadcasts-tpls-modal">
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
              <h3 className="font-display text-xl flex items-center gap-2"><FolderOpen className="w-5 h-5" /> Vorlagen</h3>
              <button onClick={() => setTplsOpen(false)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto p-5 space-y-2">
              {templates.length === 0 ? (
                <div className="text-sm opacity-60">Noch keine Vorlagen. Speichere unten im Compose-Bereich eine ab.</div>
              ) : templates.map((t) => (
                <div key={t.id} className="p-3 rounded-lg border flex items-center gap-3" style={{ borderColor: "var(--wp-border)" }}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-[11px] opacity-60 truncate">{t.subject || "(kein Betreff)"}</div>
                  </div>
                  <button onClick={() => applyTemplate(t)} className="btn-secondary text-xs !py-1.5" data-testid={`broadcasts-tpl-load-${t.id}`}>Laden</button>
                  <button onClick={() => deleteTemplate(t.id)} className="btn-secondary text-xs !py-1.5 text-red-600" title="Löschen" data-testid={`broadcasts-tpl-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
